import type { ComponentType } from "react";
import React, { useEffect, useContext, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { reportService } from "@/shared/services/reportService";
import { renderTemplate } from "@/shared/helpers/reportHelper";
import NoDataFound from "@/components/NoDataFound";
import LocationDetails from "../components/LocationDetails";
import { BrandDigitalCampaignList as BrandDigitalCampaignListBase } from "@/features/campaigns/digital/brand-digital-campaign-list";
import PageLayout from "@/components/layouts/page/PageLayout";
import BackLink from "@/components/BackLink";
import PageHeader from "@/components/layouts/pageHeaders/PageHeader";
import UniversalWidgetTemplateBase from "@/components/templates/UniversalWidgetTemplate";
import useFetcher from "@/helpers/hooks/useFetcher";
import { CUSTOM_WIDGET_PARAM } from "@/config/constants";
import { Button } from "@mui/material";
import { getDashboardPath } from "@/helpers/utils";

const LocationReport = () => {
  const UniversalWidgetTemplate =
    UniversalWidgetTemplateBase as ComponentType<any>;
  const BrandDigitalCampaignList =
    BrandDigitalCampaignListBase as ComponentType<any>;
  const { locationSysId, partId } = useParams();
  const { selectedBrand } = useContext(GlobalContext);
  const loader = useContext(LoaderContext);

  const reqData = useMemo(() => {
    return {
      system_generated_location_id: locationSysId,
      fk_participant_id: partId,
      fk_brand_id: selectedBrand?.id,
      dataType: "location",
    };
  }, [locationSysId, selectedBrand?.id, partId]);

  const { data: report, isLoading: loading } = useFetcher(
    reqData as any,
    reportService.getLocationReport,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  const renderChartWidget = () => {};

  const renderTable = (widget: any) => {
    if (!widget.data || widget.data.length === 0) {
      return <NoDataFound message="There is no record" />;
    }

    return (
      <BrandDigitalCampaignList
        campaignReportList={widget.data}
        showFilter={true}
        extraData={{ locationSysId, partId }}
        fromPage="location_report"
        hideSearchLabel={false}
        fetchCampaignsReportData={() => {}}
        totalRecords={widget?.data?.totalCountOfCampaign}
      />
    );
  };

  return (
    <PageLayout className="location-report-container mx-0 mx-sm-2">
      <BackLink />
      <PageHeader title="Location Report" />
      <div className="report admin-location-report mt-3 mb-5">
        {loading && <NoDataFound message="Loading..." />}
        {!loading && !report && (
          <NoDataFound
            message={
              <div>
                <div>No data found!</div>
                <Button
                  component={Link}
                  to={`${getDashboardPath()}`}
                  variant="contained"
                  color="primary"
                  className="my-4"
                  size="large"
                >
                  Back to dashboard
                </Button>
              </div>
            }
          />
        )}
        {!loading && report && (
          <>
            <LocationDetails report={report} />
            <UniversalWidgetTemplate
              rows={report?.template?.universal_widget_rows}
              type="location"
              requestParam={{ system_generated_location_id: locationSysId }}
              embedWidgetParams={{
                [CUSTOM_WIDGET_PARAM.LocationID]: locationSysId,
              }}
              hideDateSelector
            />
            {renderTemplate({
              report,
              renderTable,
              renderChartWidget,
              embedWidgetParams: {
                [CUSTOM_WIDGET_PARAM.LocationID]: locationSysId,
              },
            })}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default LocationReport;
